import { useState, Fragment, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { api, channelName, Job } from '../lib/api'
import HudLoader from '../components/HudLoader'
import { LanguageSelect, LanguageMultiSelect, MarketMultiSelect, csvToArr } from '../lib/i18n'
import {
  Plus,
  Filter,
  MoreVertical,
  Play,
  Pause,
  RotateCcw,
  RefreshCw,
  X,
  Eye,
  Trash2,
  Loader2,
  AlertCircle,
  CheckCircle,
  Clock,
  Zap,
} from 'lucide-react'
import { format, formatDistanceToNow } from 'date-fns'

const STATUS_COLORS: Record<string, string> = {
  pending: 'badge-secondary',
  running: 'badge-info',
  completed: 'badge-success',
  failed: 'badge-danger',
  cancelled: 'badge-secondary',
  retrying: 'badge-warning',
}

const STAGE_ICONS: Record<string, React.ComponentType<any>> = {
  queued: Clock,
  research: Zap,
  script: Zap,
  voice: Zap,
  visual: Zap,
  assemble: Zap,
  metadata: Zap,
  compliance: Zap,
  publish: Zap,
  completed: CheckCircle,
  failed: AlertCircle,
}

export default function Jobs() {
  const queryClient = useQueryClient()
  const [selectedChannel, setSelectedChannel] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [typeFilter, setTypeFilter] = useState<string>('')
  const [expandedJob, setExpandedJob] = useState<string | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [createForm, setCreateForm] = useState({
    channel_id: '',
    language: 'en',
    target_countries: 'US,IN,GB',
    target_languages: 'en,hi,es',
    target_duration: 600,
    style: 'educational',
    privacy: 'unlisted',
    auto_fix_policy: false,
    dub_languages: 'hi,en,es',
    dub_visibility: 'unlisted',
    dub_upload: false,
    subtitles: true,
    auto_publish_hours: 24,
    shorts: true,
    shorts_count: 1,
    shorts_visibility: 'unlisted',
  })

  const { data: jobs, isLoading, isFetching, refetch, error } = useQuery({
    queryKey: ['jobs', selectedChannel, statusFilter, typeFilter],
    queryFn: () => {
      const params = new URLSearchParams()
      if (selectedChannel) params.set('channel_id', selectedChannel)
      if (statusFilter) params.set('status', statusFilter)
      if (typeFilter) params.set('job_type', typeFilter)
      params.set('limit', '100')
      return api.get<Job[]>(`/jobs?${params.toString()}`)
    },
    placeholderData: keepPreviousData,
    // Smart polling: refresh every 10s ONLY while the modal is closed AND
    // a job is actually active. No churn otherwise (this flickered before).
    refetchInterval: (query) => {
      if (showCreateModal) return false
      const list = (query.state.data as Job[] | undefined) || []
      const active = list.some((j) => ['pending', 'running', 'retrying'].includes(j.status))
      return active ? 10000 : false
    },
  })

  const { data: channels } = useQuery({
    queryKey: ['channels'],
    queryFn: () => api.get<{ id: string; title: string }[]>('/channels'),
  })

  // Preselect the channel when the modal opens and nothing is chosen yet.
  useEffect(() => {
    if (showCreateModal && !createForm.channel_id && channels?.length) {
      setCreateForm((f) => ({ ...f, channel_id: f.channel_id || channels[0].id }))
    }
  }, [showCreateModal, channels])

  const cancelMutation = useMutation({
    mutationFn: (id: string) => api.post(`/jobs/${id}/cancel`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['jobs'] }),
  })

  // 2.2.43 job-pause (user demand): ⏸ running job + ▶ resume. Pause
  // stage-boundary par lagti hai (slot chhod ke) — baaki jobs chalti rahen.
  const pauseMutation = useMutation({
    mutationFn: (id: string) => api.post(`/jobs/${id}/pause`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['jobs'] }),
    onError: (e: any) => alert(`Pause nahi hui: ${e.message}`),
  })

  const resumeMutation = useMutation({
    mutationFn: (id: string) => api.post(`/jobs/${id}/resume`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['jobs'] }),
    onError: (e: any) => alert(`Resume nahi hua: ${e.message}`),
  })

  const retryMutation = useMutation({
    mutationFn: (id: string) => api.post(`/jobs/${id}/retry`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['jobs'] }),
    onError: (e: any) => alert(`Retry nahi hui: ${e.message}`),
  })

  const resetMutation = useMutation({
    mutationFn: (id: string) => api.post(`/jobs/${id}/reset`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['jobs'] }),
    onError: (e: any) => alert(`Reset nahi hua: ${e.message}`),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/jobs/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['jobs'] }),
    onError: (e: any) => alert(`Delete nahi hui: ${e.message}`),
  })

  const runPipelineMutation = useMutation({
    mutationFn: (data: {
      channel_id: string
      topic_id?: string
      video_id?: string
      language: string
      target_countries: string
      target_languages: string
      target_duration: number
      style: string
      privacy: string
      dub_languages?: string
      dub_visibility?: string
      dub_upload?: boolean
      subtitles?: boolean
      auto_publish_hours?: number
      shorts?: boolean
      shorts_count?: number
      shorts_visibility?: string
    }) => api.post('/jobs/run-full-pipeline', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] })
      setShowCreateModal(false)
    },
    onError: (e: any) => alert(`Job start nahi hui: ${e.message}`),
  })

  const getStatusBadge = (status: string) => (
    <span className={`badge ${STATUS_COLORS[status] || 'badge-secondary'}`}>
      {status}
    </span>
  )

  const getStageIcon = (stage: string) => {
    const Icon = STAGE_ICONS[stage] || Clock
    return <Icon size={14} />
  }

  const formatProgress = (progress: number, status: string, paused?: boolean) => {
    if (paused && ['pending', 'running', 'retrying'].includes(status)) return 'Paused'
    if (status === 'completed') return '100%'
    if (status === 'failed') return 'Failed'
    if (status === 'cancelled') return 'Cancelled'
    return `${progress}%`
  }

  const fmtAbs = (iso: string | null) => (iso ? format(new Date(iso), 'dd MMM, HH:mm') : '-')

  const formatElapsed = (start: string | null, end: string | null) => {
    if (!start) return '-'
    const ms = new Date(end || Date.now()).getTime() - new Date(start).getTime()
    if (ms < 0) return '-'
    const m = Math.floor(ms / 60000)
    if (m < 1) return '<1 min'
    if (m < 60) return `${m} min`
    const h = Math.floor(m / 60)
    return `${h}h ${m % 60}m`
  }

  const handleCreateSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!createForm.channel_id) {
      alert('Pehle Channel select karo (khali hai to Channels page me add karo).')
      return
    }
    runPipelineMutation.mutate(createForm)
  }

  if (isLoading) return <HudLoader text="Loading jobs" />

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Jobs</h1>
          <p>Monitor and manage automation pipelines</p>
        </div>
        <div className="header-filters">
          <select value={selectedChannel} onChange={(e) => setSelectedChannel(e.target.value)} className="select" style={{ minWidth: 180 }}>
            <option value="">All Channels</option>
            {channels?.map((c: any) => <option key={c.id} value={c.id}>{c.title}</option>)}
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="select" style={{ minWidth: 150 }}>
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="running">Running</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
            <option value="cancelled">Cancelled</option>
            <option value="retrying">Retrying</option>
          </select>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="select" style={{ minWidth: 180 }}>
            <option value="">All Types</option>
            <option value="full_pipeline">Full Pipeline</option>
            <option value="publish_only">Publish Only</option>
            <option value="dub_only">Dub Only</option>
          </select>
          <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
            <Plus size={18} />
            Run Pipeline
          </button>
          <button className="btn btn-secondary" onClick={() => refetch()} disabled={isFetching} title="Refresh job list">
            {isFetching ? <Loader2 size={18} className="spin" /> : <RefreshCw size={18} />}
            {isFetching ? 'Updating...' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="card">
        {error && (
          <div className="alert alert-error" style={{ marginBottom: 14 }}>
            <AlertCircle size={18} />
            <span>Jobs load nahi hui: {(error as Error).message}</span>
            <button className="btn btn-secondary btn-sm" onClick={() => refetch()}>Retry</button>
          </div>
        )}
<div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Type</th>
                <th>Channel</th>
                <th>Status</th>
                <th>Stage</th>
                <th>Progress</th>
                <th>Created</th>
                <th>Started</th>
                <th>Ended · Time</th>
                <th>Retries</th>
                <th>Error</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {jobs?.map((job) => (
                <Fragment key={job.id}>
                <tr className={expandedJob === job.id ? 'expanded' : ''}>
                  <td>
                    <span className="job-type">{job.job_type.replace('_', ' ')}</span>
                  </td>
                  <td>
                    <div>
                      <span className="topic-title">{channelName(channels, job.channel_id)}</span>
                      <span className="topic-desc">{job.channel_id.slice(0, 8)}…</span>
                    </div>
                  </td>
                  <td>
                    {getStatusBadge(job.status)}
                    {job.paused && (
                      <span className="badge badge-warning" title="User ne ⏸ dabaya — Resume dabao wapas chalane ke liye" style={{ marginLeft: 6 }}>
                        paused
                      </span>
                    )}
                  </td>
                  <td>
                    <div className="stage-cell">
                      {getStageIcon(job.current_stage)}
                      <span>{job.current_stage.replace('_', ' ')}</span>
                    </div>
                  </td>
                  <td>
                    <div className="progress-bar">
                      <div className="progress-fill" style={{ width: `${job.progress_pct}%` }} />
                    </div>
                    <span className="progress-text">{formatProgress(job.progress_pct, job.status, job.paused)}</span>
                  </td>
                  <td title={job.created_at ? `Created: ${new Date(job.created_at).toLocaleString()}` : ''}>
                    {job.created_at ? fmtAbs(job.created_at) : '-'}
                  </td>
                  <td title={job.started_at ? `Started: ${new Date(job.started_at).toLocaleString()}` : ''}>
                    {job.started_at ? fmtAbs(job.started_at) : 'Not started'}
                    {job.started_at && (
                      <span className="topic-desc">{formatDistanceToNow(new Date(job.started_at), { addSuffix: true })}</span>
                    )}
                  </td>
                  <td title={job.completed_at ? `Ended: ${new Date(job.completed_at).toLocaleString()}` : ''}>
                    {job.completed_at ? fmtAbs(job.completed_at) : formatElapsed(job.started_at, job.completed_at)}
                  </td>
                  <td>{job.retry_count}/{job.max_retries}</td>
                  <td>
                    {job.error_message ? (
                      <span className="error-text" title={job.error_message}>
                        <AlertCircle size={12} />
                        {job.error_message.substring(0, 50)}...
                      </span>
                    ) : '-'}
                  </td>
                  <td>
                    <div className="action-menu">
                      <button className="icon-btn" onClick={() => setExpandedJob(expandedJob === job.id ? null : job.id)} title="Details">
                        <Eye size={16} />
                      </button>
                      {['pending', 'running', 'retrying'].includes(job.status) && !job.paused && (
                        <button
                          className="icon-btn"
                          onClick={() => pauseMutation.mutate(job.id)}
                          disabled={pauseMutation.isPending}
                          title="Pause — current stage khatam hone ke baad rukegi (slot chhod ke, baaki jobs chalti rahen)"
                        >
                          <Pause size={16} />
                        </button>
                      )}
                      {['pending', 'running', 'retrying'].includes(job.status) && job.paused && (
                        <button
                          className="icon-btn"
                          onClick={() => resumeMutation.mutate(job.id)}
                          disabled={resumeMutation.isPending}
                          title="Resume — job wapas chalegi (done stages skip)"
                        >
                          <Play size={16} />
                        </button>
                      )}
                      {['pending', 'running', 'retrying'].includes(job.status) && (
                        <button className="icon-btn danger" onClick={() => cancelMutation.mutate(job.id)} disabled={cancelMutation.isPending} title="Cancel">
                          <X size={16} />
                        </button>
                      )}
                      {job.status === 'failed' && (
                        <button className="icon-btn" onClick={() => retryMutation.mutate(job.id)} disabled={retryMutation.isPending} title="Retry">
                          <RotateCcw size={16} />
                        </button>
                      )}
                      {job.status === 'failed' && job.retry_count >= job.max_retries && (
                        <button className="icon-btn" onClick={() => resetMutation.mutate(job.id)} disabled={resetMutation.isPending} title="Reset retries (3/3 khatam) + dobara chalao">
                          <Zap size={16} />
                        </button>
                      )}
                      <button
                        className="icon-btn danger"
                        onClick={() => {
                          if (['pending', 'running', 'retrying'].includes(job.status)) {
                            alert('Job chal rahi hai — pehle Cancel karo, phir delete.')
                            return
                          }
                          if (window.confirm(`Job (${job.job_type.replace('_', ' ')}) delete karo? Logs samet hamesha ke liye jayegi.`)) {
                            deleteMutation.mutate(job.id)
                          }
                        }}
                        disabled={deleteMutation.isPending}
                        title="Delete job"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
                {expandedJob === job.id && (
                  <tr className="expanded-row">
                    <td colSpan={11}>
                      {job.error_message && (
                        <div className="job-error-full">
                          <h4>Error (full — copy karke bhejo)</h4>
                          <pre className="error-box">{job.error_message}</pre>
                        </div>
                      )}
                      <div className="job-logs">
                        <h4>Stage Logs</h4>
                        {job.logs?.map((log) => (
                          <div key={log.id} className={`log-entry log-${log.level.toLowerCase()}`}>
                            <span className="log-time">{format(new Date(log.created_at), 'HH:mm:ss')}</span>
                            <span className="log-stage">{log.stage}</span>
                            <span className="log-level">{log.level}</span>
                            <span className="log-message">{log.message}</span>
                          </div>
                        ))}
                        {!job.logs?.length && <p className="no-logs">No logs yet</p>}
                      </div>
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
        {!jobs?.length && !isFetching && (
          <div className="empty-state">
            <Zap size={48} />
            <h3>No jobs yet</h3>
            <p>Run a pipeline to see jobs here</p>
            <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
              <Plus size={18} />
              Run Pipeline
            </button>
          </div>
        )}
      </div>

      {/* Create Job Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Run Full Pipeline</h2>
              <button className="icon-btn" onClick={() => setShowCreateModal(false)}><X size={20} /></button>
            </div>
            <form onSubmit={handleCreateSubmit} className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Channel *</label>
                  <select value={createForm.channel_id} onChange={(e) => setCreateForm({...createForm, channel_id: e.target.value})} required>
                    <option value="">Select channel</option>
                    {channels?.map((c: any) => <option key={c.id} value={c.id}>{c.title}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Language (50+ supported)</label>
                  <LanguageSelect value={createForm.language} onChange={(v) => setCreateForm({...createForm, language: v})} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Target Countries — tick jo chahiye</label>
                  <MarketMultiSelect
                    value={csvToArr(createForm.target_countries)}
                    onChange={(v) => setCreateForm({...createForm, target_countries: v.join(',')})}
                  />
                </div>
                <div className="form-group">
                  <label>Target Languages (metadata localization)</label>
                  <LanguageMultiSelect
                    value={csvToArr(createForm.target_languages)}
                    onChange={(v) => setCreateForm({...createForm, target_languages: v.join(',')})}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Target Duration (seconds)</label>
                  <input type="number" value={createForm.target_duration} onChange={(e) => setCreateForm({...createForm, target_duration: parseInt(e.target.value)})} min={30} max={3600} />
                </div>
                <div className="form-group">
                  <label>Style</label>
                  <select value={createForm.style} onChange={(e) => setCreateForm({...createForm, style: e.target.value})}>
                    <option value="educational">Educational</option>
                    <option value="tutorial">Tutorial</option>
                    <option value="review">Review</option>
                    <option value="news">News</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Privacy</label>
                  <select value={createForm.privacy} onChange={(e) => setCreateForm({...createForm, privacy: e.target.value})}>
                    <option value="unlisted">Unlisted first — phir auto-publish</option>
                    <option value="private">Private</option>
                    <option value="public">Public (unlisted-first rule se hi jayega)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Auto-fix Policy Issues</label>
                  <select value={createForm.auto_fix_policy ? 'true' : 'false'} onChange={(e) => setCreateForm({...createForm, auto_fix_policy: e.target.value === 'true'})}>
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Dub Languages — tick jo chahiye (voiceover per language)</label>
                  <LanguageMultiSelect
                    value={csvToArr(createForm.dub_languages)}
                    onChange={(v) => setCreateForm({...createForm, dub_languages: v.join(',')})}
                  />
                  <p className="form-help">Zyada languages = lamba job + zyada translation quota. Shuru me 3-5 best hain.</p>
                </div>
                <div className="form-group">
                  <label>Dub Visibility</label>
                  <select value={createForm.dub_visibility} onChange={(e) => setCreateForm({...createForm, dub_visibility: e.target.value})}>
                    <option value="unlisted">Unlisted (safe)</option>
                    <option value="private">Private</option>
                    <option value="public">Public</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Upload Subtitles to Main Video</label>
                <select value={createForm.subtitles ? 'true' : 'false'} onChange={(e) => setCreateForm({...createForm, subtitles: e.target.value === 'true'})}>
                  <option value="true">Yes (all languages)</option>
                  <option value="false">No</option>
                </select>
              </div>
              <div className="form-group">
                <label>Upload Dubs as Separate Videos? (default: No — single video + subtitles)</label>
                <select value={createForm.dub_upload ? 'true' : 'false'} onChange={(e) => setCreateForm({...createForm, dub_upload: e.target.value === 'true'})}>
                  <option value="false">No — audio tracks + subtitles only</option>
                  <option value="true">Yes — separate video per language</option>
                </select>
              </div>
              <div className="form-group">
                <label>Auto-Public After Upload (hours, 0 = manual)</label>
                <input type="number" value={createForm.auto_publish_hours} onChange={(e) => setCreateForm({...createForm, auto_publish_hours: parseFloat(e.target.value) || 0})} min={0} max={720} />
                <p className="form-help">YouTube khud public kar dega — review window ke baad.</p>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Auto Shorts (vertical, #Shorts)</label>
                  <select value={createForm.shorts ? 'true' : 'false'} onChange={(e) => setCreateForm({...createForm, shorts: e.target.value === 'true'})}>
                    <option value="true">Yes — 1 Short per video (subs growth)</option>
                    <option value="false">No</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Shorts Visibility</label>
                  <select value={createForm.shorts_visibility} onChange={(e) => setCreateForm({...createForm, shorts_visibility: e.target.value})}>
                    <option value="unlisted">Unlisted first — phir auto-publish</option>
                    <option value="public">Public (unlisted-first rule se hi jayega)</option>
                    <option value="private">Private</option>
                  </select>
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={runPipelineMutation.isPending}>
                  {runPipelineMutation.isPending ? 'Starting...' : 'Start Pipeline'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
